<template>
  <div>
    <div class="form-section group">
      <span class="icon">
        <icon-colored-pollen />
      </span>
      {{ pollenTargetingTitle }}
      <div
        v-if="canBeRemoved"
        class="actions">
        <button
          class="btn btn-danger btn-sm"
          :disabled="isReadOnly"
          @click="clear">
          <icon-delete />
          {{ actionRemoveTitle }}
        </button>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isMounted"
        class="form-section-content">
        <div class="form-group row">
          <div
            :id="formLabelId"
            class="col-sm-12">
            <pollen-targeting-editor
              :wrapped-in-pane="false"
              :is-read-only="isReadOnly"
              :selected-pollen-conditions="pollenTargetingConditions"
              :targeting-criteria="targetingCriteria"
              @selected-pollen-conditions-changed="selectedPollenConditionsChanged" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onMounted, onUnmounted, ref } from 'vue';

  import PollenTargetingEditor from '@/common/components/controls/targeting/pollen/PollenTargetingEditor.vue';
  import EntityFormHelper from '@/common/components/entity-form/EntityFormHelper';
  import { Criterion } from '@/common/components/entity-form/EntityType';
  import TargetingSectionHelper, {
    ITargetingCriteria,
    TargetingCode,
  } from '@/common/components/entity-form/TargetingSectionHelper';
  import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
  import IconColoredPollen from '@/common/icons/colored/Pollen.vue';
  import IconDelete from '@/common/icons/monochrome/Delete.vue';
  import { LineItem, Package, Plan } from '@/common/models';
  import i18n from '@/vueI18n';

  interface IProps {
    entity: LineItem | Plan | Package;
    canBeRemoved: boolean;
    isReadOnly: boolean;
  }

  const emit = defineEmits(['cleared']);

  const props = withDefaults(defineProps<IProps>(), {
    canBeRemoved: false,
    isReadOnly: false,
  });

  const isMounted = ref(false);
  const actionRemoveTitle = computed<string>(() => capitalize(i18n.global.t('actions.remove')));
  const entityFormHelper = computed<EntityFormHelper>(() => new EntityFormHelper(props.entity));
  const entityTargetingCriteria = computed<Criterion[]>(() => props.entity.targetingCriteria || []);
  const formLabelId = computed<string>(() => entityFormHelper.value.getFormLabelId('edit', 'pollen'));
  const pollenTargetingConditions = computed({
    get() {
      const pollenCriterion = targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.Pollen);
      if (pollenCriterion && entityTargetingCriteria.value) {
        const criterion = entityTargetingCriteria.value.find((x) => x.targetingCriterionID === pollenCriterion.id);
        return criterion ? [...criterion.values].find((x) => x.code !== TargetingCode.Location).value.split(',') : null;
      }
      return null;
    },
    set(val) {
      const pollenTargetingCriteria = getPollenTargetingCriteria(val);
      const otherTargetingCriterion = targetingCriteria.value.filter((x) => x.type !== TargetingCriteriaType.Pollen);
      updateEntityCriteria([
        ...(pollenTargetingCriteria == null ? [] : pollenTargetingCriteria),
        ...(otherTargetingCriterion.length > 0
          ? entityTargetingCriteria.value.filter((x) =>
              otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
            )
          : []),
      ]);
    },
  });
  const pollenTargetingTitle = computed<string>(() => capitalize(i18n.global.t('messages.pollenTargeting')));
  const targetingCriteria = computed<ITargetingCriteria[]>(() => targetingSectionHelper.value.targetingCriteria || []);
  const targetingSectionHelper = computed<TargetingSectionHelper>(() => new TargetingSectionHelper(props.entity));

  function getPollenTargetingCriteria(conditions: string[]): Criterion[] | null {
    const criteria = [];
    if (conditions?.length > 0) {
      const pollenCriterion = targetingCriteria.value.find((x) => x.type === TargetingCriteriaType.Pollen);
      const newEntityCriteria = targetingSectionHelper.value.newEntityCriteria(props.entity.id, pollenCriterion.id, [
        {
          code: TargetingCode.PollenCategory,
          value: conditions.join(','),
        },
      ]);
      criteria.push(newEntityCriteria);
    }
    return criteria?.length > 0 ? criteria : null;
  }

  function updateEntityCriteria(value: Criterion[]): void {
    entityFormHelper.value.updateEntityCriteria(value);
  }

  function selectedPollenConditionsChanged(x: string[]): void {
    pollenTargetingConditions.value = x;
  }

  function clear(): void {
    const destinationCriteriaIDs = targetingCriteria.value
      .filter((x) => x.type === TargetingCriteriaType.Pollen)
      .map((x) => x.id);
    const newEntityCriteria = entityTargetingCriteria.value.filter(
      (x) => !destinationCriteriaIDs.includes(x.targetingCriterionID),
    );
    updateEntityCriteria(newEntityCriteria);
    emit('cleared');
  }

  onMounted(() => {
    isMounted.value = true;
  });

  onUnmounted(() => {
    isMounted.value = false;
  });
</script>
